// Admin operators view. Lists every operator (active + inactive), lets the
// logged-in operator create a new one, rename / change PIN / toggle
// active state, and soft-delete (which is the same as setting active=false).
//
// The MVP has no roles: every logged-in operator can do everything here.
// "Cannot delete self" and "cannot deactivate self" are enforced server-
// side; the FE only filters the row to make it clearer.

import { useEffect, useState } from "react";
import {
  useAdminOperators,
  useCreateOperator,
  useUpdateOperator,
  useDeleteOperator,
} from "./hooks";
import type { AdminOperator } from "./api";
import { Card } from "../../../components/Card";
import { Chip } from "../../../components/Chip";
import { Button } from "../../../components/Button";
import { Input } from "../../../components/Input";
import { Sheet } from "../../../components/Sheet";
import { PageHeader } from "../../../components/PageHeader";
import { IconButton } from "../../../components/IconButton";
import { PlusIcon, PencilIcon, TrashIcon } from "../../../components/icons";
import { useMe } from "../../auth/hooks";

export function AdminOperatorsView() {
  const me = useMe(true);
  const opsQ = useAdminOperators();

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<AdminOperator | null>(null);

  return (
    <main style={{ minHeight: "100dvh", display: "flex", flexDirection: "column" }}>
      <PageHeader
        title="Operadores"
        backHref="/more"
        actions={
          <>
            <IconButton
              icon={<PlusIcon />}
              label="Nuevo operador"
              variant="outline"
              size="md"
              onClick={() => setCreateOpen(true)}
            />
          </>
        }
      />

      <div
        style={{
          padding: "var(--space-3) var(--space-4)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-3)",
        }}
      >
        <p
          style={{
            margin: 0,
            fontSize: "var(--font-size-14)",
            color: "var(--text-secondary)",
          }}
        >
          Sesión: <strong>{me.data?.name ?? "..."}</strong> · {opsQ.data?.length ?? 0} operador
          {(opsQ.data?.length ?? 0) === 1 ? "" : "es"}
        </p>
      </div>

      <div
        style={{
          flex: 1,
          padding: "0 var(--space-4) var(--space-6)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-2)",
        }}
      >
        {opsQ.isLoading && <p>Cargando...</p>}
        {opsQ.data?.length === 0 && !opsQ.isLoading && <p>No hay operadores.</p>}
        {opsQ.data?.map((op) => (
          <OperatorRow
            key={op.id}
            op={op}
            isSelf={op.id === me.data?.id}
            onEdit={() => setEditing(op)}
          />
        ))}
      </div>

      <CreateOperatorSheet
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => setCreateOpen(false)}
      />

      <EditOperatorSheet
        op={editing}
        onClose={() => setEditing(null)}
        isSelf={editing?.id === me.data?.id}
      />
    </main>
  );
}

function OperatorRow({
  op,
  isSelf,
  onEdit,
}: {
  op: AdminOperator;
  isSelf: boolean;
  onEdit: () => void;
}) {
  const remove = useDeleteOperator();
  return (
    <Card
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "var(--space-3) var(--space-4)",
        opacity: op.active ? 1 : 0.55,
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-2)",
            flexWrap: "wrap",
          }}
        >
          <strong>{op.name}</strong>
          {isSelf && (
            <Chip variant="tag" size="sm">
              Vos
            </Chip>
          )}
          {!op.active && (
            <Chip variant="tag" size="sm">
              Inactivo
            </Chip>
          )}
        </div>
        <div
          style={{
            color: "var(--text-secondary)",
            fontSize: "var(--font-size-13)",
          }}
        >
          Creado {new Date(op.createdAt).toLocaleDateString("es-AR")}
        </div>
      </div>
      <div style={{ display: "flex", gap: "var(--space-1)", flexShrink: 0 }}>
        <IconButton
          icon={<PencilIcon />}
          label="Editar"
          variant="ghost"
          size="sm"
          onClick={onEdit}
        />
        {!isSelf && (
          <IconButton
            icon={<TrashIcon />}
            label={op.active ? "Eliminar" : "Reactivar"}
            variant="ghost"
            size="sm"
            onClick={async () => {
              if (!window.confirm(`Eliminar a ${op.name}?`)) return;
              try {
                await remove.mutateAsync(op.id);
              } catch (e) {
                window.alert((e as Error).message);
              }
            }}
            disabled={remove.isPending}
          />
        )}
      </div>
    </Card>
  );
}

function CreateOperatorSheet({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const create = useCreateOperator();

  const submit = async () => {
    setError(null);
    if (name.trim().length === 0) {
      setError("El nombre es obligatorio");
      return;
    }
    if (!/^\d{4,6}$/.test(pin)) {
      setError("El PIN tiene que tener entre 4 y 6 dígitos");
      return;
    }
    try {
      await create.mutateAsync({ name: name.trim(), pin });
      setName("");
      setPin("");
      onCreated();
    } catch (e) {
      // The server returns structured error messages; surface them.
      setError((e as Error).message);
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title="Nuevo operador">
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        <Input
          label="Nombre"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setError(null);
          }}
          autoFocus
        />
        <Input
          label="PIN (4 a 6 dígitos)"
          value={pin}
          onChange={(e) => {
            setPin(e.target.value.replace(/\D/g, "").slice(0, 6));
            setError(null);
          }}
          type="password"
          inputMode="numeric"
          autoComplete="off"
          maxLength={6}
          error={error ?? undefined}
        />
        <Button
          variant="primary"
          fullWidth
          size="lg"
          onClick={submit}
          disabled={create.isPending}
        >
          {create.isPending ? "Creando..." : "Crear operador"}
        </Button>
        <p
          style={{
            margin: 0,
            fontSize: "var(--font-size-13)",
            color: "var(--text-secondary)",
          }}
        >
          El operador podrá loguearse inmediatamente con el PIN que definas. Compartilo en persona.
        </p>
      </div>
    </Sheet>
  );
}

function EditOperatorSheet({
  op,
  onClose,
  isSelf,
}: {
  op: AdminOperator | null;
  onClose: () => void;
  isSelf: boolean;
}) {
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const update = useUpdateOperator();

  // Reset local state whenever a different operator is being edited.
  // Using an effect here is the correct place to sync external state:
  // doing it inline during render would loop.
  useEffect(() => {
    if (op) {
      setName(op.name);
      setPin("");
      setError(null);
    }
  }, [op?.id]);

  if (!op) return null;

  const submit = async () => {
    setError(null);
    const body: { name?: string; pin?: string; active?: boolean } = {};
    if (name.trim() !== op.name && name.trim().length > 0) body.name = name.trim();
    if (pin.length > 0) {
      if (!/^\d{4,6}$/.test(pin)) {
        setError("El PIN tiene que tener entre 4 y 6 dígitos");
        return;
      }
      body.pin = pin;
    }
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    try {
      await update.mutateAsync({ id: op.id, body });
      setPin("");
      onClose();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const toggleActive = async () => {
    setError(null);
    try {
      await update.mutateAsync({ id: op.id, body: { active: !op.active } });
      onClose();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <Sheet open={!!op} onClose={onClose} title={`Editar ${op.name}`}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        <Input
          label="Nombre"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setError(null);
          }}
        />
        <Input
          label="Nuevo PIN (dejar vacío para no cambiar)"
          value={pin}
          onChange={(e) => {
            setPin(e.target.value.replace(/\D/g, "").slice(0, 6));
            setError(null);
          }}
          type="password"
          inputMode="numeric"
          autoComplete="off"
          maxLength={6}
          error={error ?? undefined}
        />
        <Button
          variant="primary"
          fullWidth
          size="lg"
          onClick={submit}
          disabled={update.isPending}
        >
          {update.isPending ? "Guardando..." : "Guardar cambios"}
        </Button>
        {!isSelf && (
          <Button
            variant={op.active ? "outline" : "primary"}
            fullWidth
            onClick={toggleActive}
            disabled={update.isPending}
          >
            {op.active ? "Desactivar" : "Reactivar"}
          </Button>
        )}
        {isSelf && (
          <p
            style={{
              margin: 0,
              fontSize: "var(--font-size-13)",
              color: "var(--text-secondary)",
            }}
          >
            No podés desactivarte a vos mismo.
          </p>
        )}
      </div>
    </Sheet>
  );
}
